import React, { useState, useEffect } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';
import '../styles/enterprise.css';
import '../styles/responsive/pages/RulesEngine.css';
import { useTheme } from '../context/ThemeContext';
import PageHeaderPro from '../components/common/PageHeaderPro';
import Skeleton from '../components/common/Skeleton';
import { useGetRulesQuery, useUpdateRulesMutation } from '../store/api/adminOpsApi';

// §3 tunable defaults. `key` matches the RiskRule key the backend stores.
const FIELDS = [
  { key: 'maker_checker_threshold_usd', label: 'Maker-checker threshold', unit: 'USD', hint: 'Above this amount the payout is flagged for review. Advisory only; it does not block approval.', step: '1' },
  { key: 'address_cooldown_hours', label: 'Address cooldown', unit: 'hours', hint: 'New/changed destination addresses are blocked until cleared.', step: '1' },
  { key: 'duplicate_window_minutes', label: 'Duplicate window (DUP-3)', unit: 'minutes', hint: 'Same merchant + amount + destination within this window flags a duplicate.', step: '1' },
  { key: 'amount_anomaly_multiple', label: 'Amount anomaly multiple (AMT-4)', unit: '× rolling avg', hint: 'Amounts above this multiple of the rolling average are flagged.', step: '0.1' },
  { key: 'required_confirmations', label: 'Required confirmations', unit: 'blocks', hint: 'Default per-network confirmation depth.', step: '1' },
  { key: 'admin_lock_ttl_minutes', label: 'Admin-lock TTL (RACE-1)', unit: 'minutes', hint: 'How long a soft-claim on a request holds before expiring.', step: '1' },
  { key: 'recent_credential_change_hours', label: 'Recent credential-change window (ACCT-3)', unit: 'hours', hint: 'Credential changes within this window flag the request.', step: '1' },
];

const RulesEngine = () => {
  const { mode } = useTheme();
  const { data, isLoading } = useGetRulesQuery(undefined, { refetchOnMountOrArgChange: true });
  const [updateRules, { isLoading: saving }] = useUpdateRulesMutation();

  const [form, setForm] = useState({});

  // Seed the form from the loaded rules; backend may return { rules: {...} } or a flat map.
  useEffect(() => {
    if (!data) return;
    const rules = data.rules || data || {};
    const next = {};
    FIELDS.forEach((f) => {
      const v = rules[f.key];
      next[f.key] = v !== undefined && v !== null ? String(v) : '';
    });
    setForm(next);
  }, [data]);

  const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {};
    let invalid = false;
    FIELDS.forEach((f) => {
      const raw = form[f.key];
      if (raw === '' || raw === undefined) return;
      const num = Number(raw);
      if (Number.isNaN(num) || num < 0) {
        invalid = true;
        return;
      }
      payload[f.key] = num;
    });
    if (invalid) {
      toast.error('All values must be non-negative numbers');
      return;
    }
    try {
      await updateRules(payload).unwrap();
      toast.success('Rules updated');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update rules');
    }
  };

  return (
    <div className="ent ent-page rules-engine-page">
      <PageHeaderPro
        title="Rules Engine"
        mode={mode === 'test' ? 'test' : 'live'}
        updatedText={data?.updatedAt ? `Updated ${new Date(data.updatedAt).toLocaleString()}` : undefined}
      />

      <div className="rules-card rd-card rd-card-pad">
        <div className="rules-card__head">
          <SlidersHorizontal size={18} />
          <div>
            <h2>Tunable thresholds</h2>
            <p>Changes are audit-logged and re-verified server-side at approval.</p>
          </div>
        </div>

        {isLoading ? (
          <div className="rules-grid">
            {FIELDS.map((f) => (
              <div key={f.key} className="rd-field">
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="100%" height={40} />
              </div>
            ))}
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <div className="rules-grid">
              {FIELDS.map((f) => (
                <div key={f.key} className="rd-field">
                  <label className="rd-label" htmlFor={f.key}>
                    {f.label}
                  </label>
                  <div className="rules-input-row">
                    <input
                      id={f.key}
                      className="rd-input"
                      type="number"
                      min="0"
                      step={f.step}
                      value={form[f.key] ?? ''}
                      onChange={(e) => onChange(f.key, e.target.value)}
                    />
                    <span className="rules-unit">{f.unit}</span>
                  </div>
                  <span className="rd-hint">{f.hint}</span>
                </div>
              ))}
            </div>
            <div className="rules-actions">
              <button type="submit" className="rd-btn rd-btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RulesEngine;
